import axios from "axios";
import { useState } from "react";
import { CSVLink } from "react-csv";
import * as dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";

const headers = [
  { label: "First Name", key: "firstName" },
  { label: "Last Name", key: "lastName" },
  { label: "Gender", key: "gender" },
  { label: "Flying", key: "camperFlying" },
  { label: "Birthday", key: "birthDate" },
  { label: "Grade", key: "grade" },
  { label: "Email", key: "email" },
  { label: "Mother Name", key: "motherName" },
  { label: "Mother Number", key: "motherNumber" },
  { label: "Father Name", key: "fatherName" },
  { label: "Father Number", key: "fatherNumber" },
  { label: "Medical Conditions", key: "medicalConditions" },
  { label: "Regular Medications", key: "regularMedications" },
  { label: "Shirt Size", key: "shirtSize" },
  { label: "Address", key: "streetAddress" },
  { label: "City", key: "city" },
  { label: "State", key: "state" },
  { label: "Zip", key: "zip" },
];

const filters = [
  ["all", "All", () => true],
  ["unpaid", "Not paid", (c) => !c.paid],
  ["paid", "Paid", (c) => c.paid],
  ["flying", "Flying in", (c) => c.camperFlying === "yes"],
];

const capitalize = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "–");

export default function CamperTable({ campers, callback_request }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [pendingId, setPendingId] = useState(null);

  const q = query.trim().toLowerCase();
  const matchesFilter = filters.find(([key]) => key === filter)[2];
  const filteredCampers = (campers || []).filter(
    (c) =>
      matchesFilter(c) &&
      (!q || `${c.firstName} ${c.lastName}`.toLowerCase().includes(q))
  );

  const togglePaid = (e, camper) => {
    e.stopPropagation();
    setPendingId(camper._id);
    axios
      .put(
        "https://api.houstonsikhyouthcamp.org/camper",
        {
          camper_information: { camper },
          camper_paid: !camper.paid,
          camper_id: camper._id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      )
      .then(() => callback_request(true))
      .finally(() => setPendingId(null));
  };

  return (
    <section className="adm-campers" aria-labelledby="camper-list">
      <h2 id="camper-list" className="adm-visually-hidden">
        Camper list
      </h2>
      <div className="adm-toolbar">
        <label className="adm-search">
          <span className="adm-visually-hidden">Search by name</span>
          <input
            type="search"
            placeholder="Search by name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <div className="adm-chips" role="group" aria-label="Show">
          {filters.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className="adm-chip"
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        {campers && (
          <CSVLink
            className="adm-export"
            data={filteredCampers}
            filename={"campers.csv"}
            headers={headers}
          >
            Export {filteredCampers.length} to CSV
          </CSVLink>
        )}
      </div>

      <div className="adm-table">
        <table>
          <caption className="adm-visually-hidden">
            HSYC 2026 registered campers
          </caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Singh/Kaur</th>
              <th scope="col">Date of birth</th>
              <th scope="col" className="adm-num">Age</th>
              <th scope="col">Grade</th>
              <th scope="col">Status</th>
              <th scope="col">Flying in</th>
              <th scope="col">Paying by</th>
              <th scope="col">Paid</th>
            </tr>
          </thead>
          <tbody>
            {filteredCampers.map((camper) => (
              <tr
                key={camper._id}
                onClick={() => navigate(`/admin/camper/${camper._id}`)}
              >
                <td>
                  <Link
                    to={`/admin/camper/${camper._id}`}
                    className="adm-name"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {camper.firstName} {camper.lastName}
                  </Link>
                </td>
                <td>{camper.gender}</td>
                <td>
                  {camper.month}/{camper.day}/{camper.year}
                </td>
                <td className="adm-num">
                  {dayjs().diff(
                    dayjs(`${camper.year}-${camper.month}-${camper.day}`),
                    "year"
                  )}
                </td>
                <td>{camper.grade?.replace("Grade ", "")}</td>
                <td>{camper.status}</td>
                <td>{camper.camperFlying === "yes" ? "Yes" : "No"}</td>
                <td>{capitalize(camper.camperPayment)}</td>
                <td>
                  <button
                    type="button"
                    className={`adm-paid ${camper.paid ? "is-paid" : ""}`}
                    aria-label={`${camper.firstName} ${camper.lastName}: ${
                      camper.paid ? "paid, mark as not paid" : "not paid, mark as paid"
                    }`}
                    disabled={pendingId === camper._id}
                    onClick={(e) => togglePaid(e, camper)}
                  >
                    {pendingId === camper._id
                      ? "Saving…"
                      : camper.paid
                      ? "Paid"
                      : "Not paid"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {campers && filteredCampers.length === 0 && (
          <p className="adm-empty">
            {campers.length === 0
              ? "No one has registered yet. New registrations show up here right away."
              : "No campers match. Clear the search or choose All."}
          </p>
        )}
        {!campers && <p className="adm-empty">Loading campers…</p>}
      </div>
    </section>
  );
}
